/**
 * ManualGeoFixModal — pin a location on a Leaflet map when Nominatim
 * couldn't resolve it. Saves via POST /api/geocoding/manual-fix.
 */
import React, { useState } from "react";
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { X, Loader2, Search, MapPin, CheckCircle2 } from "lucide-react";

const pinIcon = L.divIcon({
  className: "",
  html: `<div style="width:28px;height:28px;background:#FFC60A;border:3px solid #0A0A0B;border-radius:50% 50% 50% 0;transform:rotate(-45deg);box-shadow:2px 2px 0 0 #0A0A0B;"></div>`,
  iconSize: [28, 28],
  iconAnchor: [14, 26],
});

function ClickCapture({ onPick }) {
  useMapEvents({ click: (e) => onPick([e.latlng.lat, e.latlng.lng]) });
  return null;
}

function Recenter({ center }) {
  const map = useMap();
  React.useEffect(() => { if (center) map.setView(center, 16); }, [center, map]);
  return null;
}

export default function ManualGeoFixModal({ name, onClose, onSaved }) {
  const [pos, setPos] = useState(null);
  const [center, setCenter] = useState(null);
  const [query, setQuery] = useState(name.split(",")[0] || "");
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);

  const search = async () => {
    if (query.trim().length < 2) return;
    setSearching(true);
    try {
      const r = await api.post("/geocode", { address: query.trim() });
      const res = r.data?.result;
      if (res?.lat != null) {
        setCenter([res.lat, res.lon]);
        toast.success("Carte recentrée — cliquez pour placer le point exact");
      } else {
        toast.error("Adresse introuvable, essayez le nom du village");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Recherche impossible");
    } finally { setSearching(false); }
  };

  const save = async () => {
    if (!pos) { toast.error("Cliquez d'abord sur la carte pour placer le point"); return; }
    setSaving(true);
    try {
      const r = await api.post("/geocoding/manual-fix", { name, lat: pos[0], lon: pos[1] });
      toast.success(`« ${name} » localisé — ${r.data.updated_template_days} trames + ${r.data.updated_planned_services} services mis à jour`);
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[1000] bg-black/60 flex items-center justify-center p-4" data-testid="manual-geofix-modal">
      <div className="bg-white border-2 border-zinc-900 shadow-[6px_6px_0_0_#0A0A0B] w-full max-w-2xl flex flex-col max-h-[90vh]">
        <div className="flex items-center gap-2 px-3 py-2 bg-zinc-900 text-white flex-shrink-0">
          <MapPin size={14} className="text-[#FFC60A]" />
          <div className="text-[10px] font-mono tracking-[0.2em] uppercase truncate">Localiser : {name}</div>
          <div className="flex-1" />
          <button onClick={onClose} data-testid="manual-geofix-close" className="p-1 hover:bg-zinc-800"><X size={14} /></button>
        </div>

        <div className="p-3 flex gap-2 border-b border-zinc-200 flex-shrink-0">
          <input value={query} onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && search()}
            placeholder="Rechercher un village / adresse pour recentrer la carte…"
            data-testid="manual-geofix-search-input"
            className="flex-1 border border-zinc-900 px-3 py-1.5 text-sm" />
          <button onClick={search} disabled={searching} data-testid="manual-geofix-search-btn"
            className="px-3 py-1.5 bg-zinc-900 text-[#FFC60A] text-[10px] font-mono uppercase tracking-widest inline-flex items-center gap-1 disabled:opacity-50">
            {searching ? <Loader2 size={12} className="animate-spin" /> : <Search size={12} />} Chercher
          </button>
        </div>

        <div className="h-[380px] flex-shrink-0" data-testid="manual-geofix-map">
          <MapContainer center={[46.52, 6.45]} zoom={11} style={{ height: "100%", width: "100%" }}>
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; OpenStreetMap' />
            <ClickCapture onPick={setPos} />
            {center && <Recenter center={center} />}
            {pos && <Marker position={pos} icon={pinIcon} />}
          </MapContainer>
        </div>

        <div className="p-3 flex items-center gap-3 flex-shrink-0">
          {pos ? (
            <div className="text-[11px] font-mono text-emerald-700 inline-flex items-center gap-1.5" data-testid="manual-geofix-coords">
              <CheckCircle2 size={12} /> {pos[0].toFixed(5)}, {pos[1].toFixed(5)}
            </div>
          ) : (
            <div className="text-[11px] text-zinc-500">Cliquez sur la carte pour placer le point exact</div>
          )}
          <div className="flex-1" />
          <button onClick={onClose} className="px-3 py-1.5 border border-zinc-300 text-[10px] font-bold uppercase tracking-widest hover:bg-zinc-100">Annuler</button>
          <button onClick={save} disabled={!pos || saving} data-testid="manual-geofix-save"
            className="px-4 py-1.5 bg-[#FFC60A] text-zinc-900 border border-zinc-900 text-[10px] font-black uppercase tracking-widest hover:bg-yellow-300 inline-flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed">
            {saving ? <Loader2 size={12} className="animate-spin" /> : <MapPin size={12} />} Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
}
